import type {
  Board,
  BoardPage,
  BoardView,
  TicketRead,
  Column,
  Dependency,
  LegacyBoardDetail,
  LegacyNode,
  Ticket,
  TicketPage,
} from "./contracts";
import { ApiProblem, request } from "./transport";

export interface BoardCache {
  board: Board;
  boardEtag: string;
  columns: Column[];
  columnsById: Map<string, Column>;
  columnsByKey: Map<string, Column>;
  tickets: Map<string, TicketRead>;
  dependencies: Map<string, Dependency[]>;
  document: LegacyBoardDetail;
}

const caches = new Map<string, BoardCache>();
const reads = new Map<string, Promise<unknown>>();
function serializeRead<T>(boardId: string, operation: () => Promise<T>): Promise<T> {
  const pending = (reads.get(boardId) || Promise.resolve()).catch(() => {}).then(operation);
  reads.set(boardId, pending);
  void pending.finally(() => { if (reads.get(boardId) === pending) reads.delete(boardId); }).catch(() => {});
  return pending;
}

export function prefixFromTicket(ticket: Pick<Ticket, "key" | "number">): string {
  const match = ticket.key.match(/^([A-Za-z0-9]{1,4})-([0-9]+)$/);
  return match && Number(match[2]) === ticket.number ? match[1]! : "BLD";
}

export function cacheFor(boardId: string): BoardCache | undefined {
  return caches.get(boardId);
}

export async function listBoards(): Promise<Board[]> {
  const items: Board[] = [];
  let cursor: string | undefined;
  do {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}&limit=200` : "?limit=200";
    const page = (await request<BoardPage>("GET", `/api/v1/boards${query}`)).body;
    items.push(...page.items);
    cursor = page.next_cursor || undefined;
  } while (cursor);
  return items;
}

async function listTickets(boardId: string): Promise<Ticket[]> {
  const items: Ticket[] = [];
  let cursor: string | undefined;
  do {
    const params = new URLSearchParams({ limit: "200" });
    if (cursor) params.set("cursor", cursor);
    const page = (
      await request<TicketPage>("GET", `/api/v1/boards/${boardId}/tickets?${params}`)
    ).body;
    items.push(...page.items);
    cursor = page.next_cursor || undefined;
  } while (cursor);
  return items;
}

function buildDocument(
  board: Board,
  columns: Column[],
  tickets: TicketRead[],
  dependencies: Map<string, Dependency[]>,
): LegacyBoardDetail {
  const columnById = new Map(columns.map((column) => [column.id, column]));
  const nodes = new Map<string, LegacyNode>();
  for (const ticket of tickets) {
    const metadata = { ...(ticket.metadata || {}) };
    delete metadata.note;
    delete metadata.notePreview;
    nodes.set(ticket.id, {
      ...metadata,
      id: ticket.id,
      type: ticket.type,
      title: ticket.title,
      ...(typeof ticket.note === "string" ? { note: ticket.note } : {}),
      ...(typeof ticket.note !== "string" ? { notePreview: ticket.note_preview || "" } : {}),
      priority: ticket.priority,
      area: ticket.area,
      expanded: ticket.expanded,
      bucket: ticket.timeline_bucket,
      ticketNumber: ticket.number,
      ticketPrefix: prefixFromTicket(ticket),
      agentWorkflowActive: ticket.agent_workflow_active,
      agentWorkflow: ticket.agent_workflow,
      blockedBy: (dependencies.get(ticket.id) || []).map((edge) => edge.blocking_ticket_id),
      relatedTo: Array.isArray(metadata.relatedTo) ? metadata.relatedTo : [],
      attachments: [],
      children: [],
      ...(ticket.parent_id
        ? { status: ticket.status }
        : { col: columnById.get(ticket.column_id || "")?.key || "backlog" }),
    });
  }
  const ordered = [...tickets].sort(
    (left, right) => left.sort_order - right.sort_order || left.number - right.number,
  );
  const cards: LegacyNode[] = [];
  for (const ticket of ordered) {
    const node = nodes.get(ticket.id);
    if (!node) continue;
    if (ticket.parent_id && nodes.has(ticket.parent_id)) {
      nodes.get(ticket.parent_id)?.children?.push(node);
    } else {
      cards.push(node);
    }
  }
  return {
    id: board.id,
    name: board.name,
    role: board.role,
    state: {
      cards,
      view: board.settings.view,
      tlUnit: board.settings.timeline_unit,
    },
    version: board.version,
    storage_mode: board.storage_mode,
    columns: columns.map((column) => ({
      id: column.key,
      name: column.name,
      terminal: column.terminal,
    })),
  };
}

async function fetchLegacyBoard(
  boardId: string,
  updateCache: boolean,
  initialBoard?: Board,
): Promise<LegacyBoardDetail> {
  // Bootstrap already has the complete, versioned board from listBoards.
  // Reuse it for this initial read only; later reconciliation fetches afresh.
  const boardResponse = initialBoard?.id === boardId
    ? { body: initialBoard, etag: String(initialBoard.version) }
    : await request<Board>("GET", `/api/v1/boards/${boardId}`);
  const board = boardResponse.body;
  if (board.storage_mode !== "relational") {
    const legacy = (await request<LegacyBoardDetail>("GET", `/api/boards/${boardId}`)).body;
    legacy.storage_mode = board.storage_mode;
    if (updateCache) caches.delete(boardId);
    return legacy;
  }
  const [columnsEnvelope, tickets, dependencyEnvelope] = await Promise.all([
    request<Column[]>("GET", `/api/v1/boards/${boardId}/columns`),
    listTickets(boardId),
    request<Dependency[]>("GET", `/api/v1/boards/${boardId}/dependencies`),
  ]);
  const dependencies = new Map<string, Dependency[]>();
  for (const edge of dependencyEnvelope.body) {
    const current = dependencies.get(edge.blocked_ticket_id) || [];
    current.push(edge);
    dependencies.set(edge.blocked_ticket_id, current);
  }
  const document = buildDocument(board, columnsEnvelope.body, tickets, dependencies);
  if (updateCache) {
    caches.set(boardId, {
      board,
      boardEtag: boardResponse.etag || String(board.version),
      columns: columnsEnvelope.body,
      columnsById: new Map(columnsEnvelope.body.map((item) => [item.id, item])),
      columnsByKey: new Map(columnsEnvelope.body.map((item) => [item.key, item])),
      tickets: new Map(tickets.map((item) => [item.id, item])),
      dependencies,
      document,
    });
  }
  return document;
}

async function readBatch(boardId: string, ids: string[]): Promise<Ticket[]> {
  const result: Ticket[] = [];
  for (let offset = 0; offset < ids.length; offset += 100) {
    result.push(...(await request<Ticket[]>("POST", `/api/v1/boards/${boardId}/tickets/batch`, {
      ticket_ids: ids.slice(offset, offset + 100),
    })).body);
  }
  return result;
}

async function loadBoardSnapshot(boardId: string, initialBoard?: Board): Promise<LegacyBoardDetail> {
  if (initialBoard && initialBoard.storage_mode !== "relational") {
    return fetchLegacyBoard(boardId, true, initialBoard);
  }
  let snapshot: BoardView;
  try {
    snapshot = (await request<BoardView>("GET", `/api/v1/boards/${boardId}/view`)).body;
  } catch (error) {
    // Rolling deployments and legacy boards retain the old, complete read path.
    if (error instanceof ApiProblem && (error.status === 404 ||
        (error.status === 409 && error.problem.code === "board_migration_required"))) {
      return fetchLegacyBoard(boardId, true, initialBoard);
    }
    throw error;
  }
  const previous = caches.get(boardId);
  const tickets: TicketRead[] = snapshot.tickets.map(item => {
    const old = previous?.tickets.get(item.id);
    return { ...item, ...(!item.note_truncated ? { note: item.note_preview }
      : old?.version === item.version && typeof old.note === "string" ? { note: old.note } : {}) };
  });
  // Reconciliation must include the remote value of every previously loaded
  // note so the existing three-way merge can preserve a user's local draft.
  const changed = tickets.filter(t => t.note === undefined && typeof previous?.tickets.get(t.id)?.note === "string");
  const refreshed = new Map((await readBatch(boardId, changed.map(t => t.id))).map(t => [t.id, t]));
  const reads = tickets.map(t => refreshed.get(t.id) || t);
  const dependencies = new Map<string, Dependency[]>();
  for (const edge of snapshot.dependencies) {
    const edges = dependencies.get(edge.blocked_ticket_id) || [];
    edges.push(edge);
    dependencies.set(edge.blocked_ticket_id, edges);
  }
  const document = buildDocument(snapshot.board, snapshot.columns, reads, dependencies);
  caches.set(boardId, {
    board: snapshot.board, boardEtag: String(snapshot.board.version), columns: snapshot.columns,
    columnsById: new Map(snapshot.columns.map(c => [c.id, c])),
    columnsByKey: new Map(snapshot.columns.map(c => [c.key, c])),
    tickets: new Map(reads.map(t => [t.id, t])), dependencies, document,
  });
  return document;
}

export function loadBoard(boardId: string, initialBoard?: Board): Promise<LegacyBoardDetail> {
  return serializeRead(boardId, () => loadBoardSnapshot(boardId, initialBoard));
}

export async function matchTickets(boardId: string, query: string): Promise<string[]> {
  return (await request<string[]>("GET", `/api/v1/boards/${boardId}/ticket-matches?q=${encodeURIComponent(query)}`)).body;
}

export function loadTicketNotes(boardId: string, ids: string[]): Promise<Ticket[]> {
  return serializeRead(boardId, async () => {
    const tickets = await readBatch(boardId, ids);
    for (const ticket of tickets) replaceCachedTicket(boardId, ticket);
    return tickets;
  });
}

export async function getTicket(boardId: string, ticketId: string): Promise<Ticket> {
  return (
    await request<Ticket>("GET", `/api/v1/boards/${boardId}/tickets/${ticketId}`)
  ).body;
}

export function replaceCachedTicket(boardId: string, ticket: Ticket): void {
  const cache = caches.get(boardId);
  if (cache && (cache.tickets.get(ticket.id)?.version || 0) <= ticket.version) cache.tickets.set(ticket.id, ticket);
}

export function forgetBoard(boardId: string): void {
  caches.delete(boardId);
}
